// GlobalContext.tsx
import React, {
  createContext,
  useState,
  useEffect,
  ReactNode,
  useContext,
  useMemo,
  useCallback,
} from "react";
import { GlobalContextProps, GlobalObject } from "../util/types";
import { ThemeObjectLandscape } from "../util/constants";
import { buildSurveyTheme } from "../theme/themeFunctions/buildSurveyTheme";
import { defaultThemeV2 } from "../theme/defaultThemeV2";
import { Alert, Box } from "@mui/material";
import TransitionLoader from "../components/ui/TransitionLoader";

const GlobalContext = createContext<GlobalContextProps | undefined>(undefined);

const GlobalProvider: React.FC<{ children: ReactNode; offline?: boolean }> = ({
  children,
  offline = false,
}) => {
  const [isThemeLoaded, setIsThemeLoaded] = useState(offline);
  const [showThemeLoader, setShowThemeLoader] = useState(false);
  const [themeLoadError, setThemeLoadError] = useState(false);
  const [globalObject, setGlobalObject] = useState<GlobalObject>({
    themeData: {
      theme: { ...ThemeObjectLandscape, orientation: "" },
      normalizedTheme: defaultThemeV2,
    },
  });

  // ✅ NEW — store selected image
  const [previewImage, setPreviewImage] = useState<{
    imageUrl: string;
    isOpen: boolean;
    number_seconds?: number;
    sourceCanvas?: HTMLCanvasElement;
    altText?: string;
  }>({ imageUrl: "", isOpen: false });

  // ✅ NEW — trigger function to update image
  /* c8 ignore start */
  const previewImageEventTrigger = (
    imageUrl: string,
    isOpen: boolean,
    number_seconds?: number,
    sourceCanvas?: HTMLCanvasElement,
    altText?: string,
  ) => {
    setPreviewImage({
      imageUrl,
      isOpen,
      number_seconds: number_seconds ?? 0,
      ...(sourceCanvas ? { sourceCanvas } : {}),
      ...(altText ? { altText } : {}),
    });
  };
  /* c8 ignore end */
  /* c8 ignore start */
  const updateGlobalObject: React.Dispatch<React.SetStateAction<GlobalObject>> =
    useCallback((newObject) => {
      setGlobalObject(newObject);
    }, []);
  /* c8 ignore end */
  useEffect(() => {
    if (offline) return;

    let isActive = true;
    const loaderTimeoutId = window.setTimeout(() => {
      if (isActive) setShowThemeLoader(true);
    }, 500);

    const fetchData = async () => {
      try {
        const url = new URL(window?.location?.href);

        const pathname = url.pathname;
        const id = pathname.split("/")[1];
        const api_protocol =
          /* c8 ignore start */

          import.meta.env.VITE_MX8_SURVEY_API_PROTOCOL ?? "https";

        const api_url = `${api_protocol}://${
          import.meta.env.VITE_MX8_SURVEY_API_DOMAIN
        }/v1/theme/${id}`;
        /* c8 ignore end */
        const response = await fetch(api_url, {
          method: "GET",
        });

        if (!response.ok) {
          throw new Error(`Theme request failed with ${response.status}`);
        }

        const themeData = await response.json();
        const { normalizedTheme, legacyTheme } = buildSurveyTheme(themeData);

        if (isActive) {
          window.clearTimeout(loaderTimeoutId);
          setGlobalObject((current) => ({
            ...current,
            themeData: {
              theme: { ...legacyTheme, landscape_Question: "black" },
              normalizedTheme,
            },
          }));
          setShowThemeLoader(false);
          setIsThemeLoaded(true);
        }
        /* c8 ignore start */
      } catch (error) {
        console.error(error);
        if (isActive) {
          window.clearTimeout(loaderTimeoutId);
          setGlobalObject((current) => ({
            ...current,
            themeData: {
              theme: { ...ThemeObjectLandscape, landscape_Question: "black" },
              normalizedTheme: defaultThemeV2,
            },
          }));
          setShowThemeLoader(false);
          setThemeLoadError(true);
          setIsThemeLoaded(true);
        }
        /* c8 ignore end */
      }
    };

    void fetchData();

    return () => {
      isActive = false;
      window.clearTimeout(loaderTimeoutId);
    };
  }, [offline]);

  // ⬇️ Updated context value: now includes image state + trigger function
  const contextValue = useMemo(
    () => ({
      globalObject,
      updateGlobalObject,
      previewImage,
      previewImageEventTrigger,
    }),
    [globalObject, previewImage],
  );

  return (
    <GlobalContext.Provider value={contextValue}>
      {isThemeLoaded ? (
        <>
          {children}
          {themeLoadError && (
            <Alert severity="error" role="alert">
              {globalObject.themeData.normalizedTheme.copy.errors.generic}
            </Alert>
          )}
        </>
      ) : (
        <Box
          sx={{
            alignItems: "center",
            backgroundColor: "#ffffff",
            display: "flex",
            justifyContent: "center",
            minHeight: "100dvh",
            width: "100%",
          }}
        >
          {showThemeLoader && <TransitionLoader id="initial-theme-loader" />}
        </Box>
      )}
    </GlobalContext.Provider>
  );
};

export { GlobalProvider, GlobalContext };

export const useThemeContext = () => {
  const context = useContext(GlobalContext);
  /* c8 ignore next 3 */
  if (!context) {
    throw new Error("useThemeContext must be used inside the ThemeProvider");
  }
  const themeData = context.globalObject.themeData;
  if (themeData.normalizedTheme) {
    return context;
  }

  const normalizedTheme = themeData.theme
    ? buildSurveyTheme(themeData.theme).normalizedTheme
    : defaultThemeV2;

  return {
    ...context,
    globalObject: {
      ...context.globalObject,
      themeData: {
        ...themeData,
        normalizedTheme,
      },
    },
  };
};
